import { useState, useCallback, useEffect } from 'react';
import { authedClient } from '@/api/trpc';
import useAuth from '@/hooks/useAuth';

interface OnboardingProgress {
  completed: boolean;
  completedVideos: string[];
  overallProgress: number;
}

const DEFAULT_PROGRESS: OnboardingProgress = {
  completed: false,
  completedVideos: [],
  overallProgress: 0,
};

/**
 * Manages onboarding progress via ConfigData (ONBOARDING_PROGRESS).
 * Replicates AngularJS welcome.controller.ts progress tracking.
 */
export function useOnboardingProgress() {
  const { currentUser } = useAuth();
  const [progress, setProgress] = useState<OnboardingProgress>(DEFAULT_PROGRESS);
  const [configItemId, setConfigItemId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const companyId = currentUser?.companyId;

  const calculateProgress = useCallback((completedVideos: string[], emailVerified: boolean | null | undefined, totalVideos: number) => {
    const watchedCount = completedVideos.length;
    const verificationPoints = emailVerified ? 1 : 0;
    return Math.round(
      ((watchedCount / totalVideos) * 0.7 + verificationPoints * 0.3) * 100
    );
  }, []);

  // Load progress
  useEffect(() => {
    if (!companyId) return;

    const load = async () => {
      try {
        const item = await authedClient.configData.getByType.query({ configType: 'ONBOARDING_PROGRESS' });
        if (item) {
          setConfigItemId(item.id);
          const d = item.data as any;
          setProgress({
            completed: d?.completed || false,
            completedVideos: d?.completedVideos || [],
            overallProgress: d?.overallProgress || 0,
          });
        }
      } catch (err) {
        console.error('[useOnboardingProgress] Load error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [companyId]);

  // Save progress
  const saveProgress = useCallback(async (updated: OnboardingProgress) => {
    if (!companyId) return;

    try {
      const result = await authedClient.configData.upsert.mutate({
        configType: 'ONBOARDING_PROGRESS',
        data: updated,
      });
      if (!configItemId && result?.id) {
        setConfigItemId(result.id);
      }
    } catch (err) {
      console.error('[useOnboardingProgress] Save error:', err);
    }
  }, [companyId, configItemId]);

  const markVideoCompleted = useCallback(async (videoId: string, totalVideos: number) => {
    if (progress.completedVideos.includes(videoId)) return;

    const newCompleted = [...progress.completedVideos, videoId];
    const overallProgress = calculateProgress(newCompleted, currentUser?.emailVerified, totalVideos);
    const updated: OnboardingProgress = {
      ...progress,
      completedVideos: newCompleted,
      overallProgress,
    };

    setProgress(updated);
    await saveProgress(updated);
  }, [progress, currentUser?.emailVerified, calculateProgress, saveProgress]);

  const markOnboardingCompleted = useCallback(async () => {
    const updated: OnboardingProgress = { ...progress, completed: true };
    setProgress(updated);
    await saveProgress(updated);
  }, [progress, saveProgress]);

  return {
    progress,
    isLoading,
    markVideoCompleted,
    markOnboardingCompleted,
    calculateProgress,
  };
}
